/**
 * RegionWelcome - solo gestiona el Modal (Fase 1).
 * El tooltip (Fase 2) vive en Navbar junto al boton, sin calculos de coordenadas.
 */
import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useRegion } from '../context/RegionContext';
import { useLanguage } from '../context/LanguageContext';
import { FlagImg } from './FlagImg';

const STORAGE_KEY = 'hr-region-welcomed';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(32px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
`;
const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26,18,8,0.55);
  backdrop-filter: blur(6px);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 32px;
  animation: ${fadeIn} 0.35s ease;

  @media (min-width: 600px) {
    align-items: center;
    padding-bottom: 0;
  }

  ${({ $closing }) => $closing && css`
    animation: ${fadeOut} 0.3s ease forwards;
  `}
`;

const Modal = styled.div`
  background: #FFFDF7;
  border-radius: 24px 24px 16px 16px;
  padding: 32px 28px 28px;
  max-width: 400px;
  width: calc(100% - 32px);
  box-shadow: 0 32px 80px rgba(26,18,8,0.25);
  animation: ${slideUp} 0.4s cubic-bezier(0.16,1,0.3,1);

  @media (min-width: 600px) {
    border-radius: 24px;
  }
`;

const FlagDiv = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 12px;
  line-height: 1;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.45rem;
  font-weight: 600;
  color: #1A1208;
  margin: 0 0 8px;
  text-align: center;
`;

const Sub = styled.p`
  font-size: 0.83rem;
  color: rgba(26,18,8,0.55);
  text-align: center;
  margin: 0 0 28px;
  line-height: 1.6;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BtnPrimary = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  background: #7C3A00;
  color: #FFFDF7;
  font-weight: 700;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, transform 0.15s;
  &:hover { background: #C07020; transform: translateY(-1px); }
`;

const BtnSecondary = styled.button`
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  background: transparent;
  color: rgba(26,18,8,0.65);
  font-size: 0.85rem;
  border: 1px solid rgba(26,18,8,0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: border-color 0.2s, color 0.2s;
  &:hover { border-color: #7C3A00; color: #7C3A00; }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0;
  span { font-size: 0.72rem; color: rgba(26,18,8,0.3); white-space: nowrap; }
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(26,18,8,0.1);
  }
`;

export function RegionWelcome() {
  const { region, flag, currency, symbol, goToRegion, setShowRegionHint } = useRegion();
  const { lang, setLang } = useLanguage();
  const [phase, setPhase] = useState('idle');
  const es = lang === 'es';
  const fr = lang === 'fr';

  const meta = {
    mx: { label: 'México',        labelEn: 'Mexico',        labelFr: 'Mexique'       },
    us: { label: 'Estados Unidos', labelEn: 'United States', labelFr: 'États-Unis'   },
    ca: { label: 'Canadá',        labelEn: 'Canada',        labelFr: 'Canada'        },
  };
  const regionOrder  = ['mx', 'us', 'ca'];
  const otherRegions = regionOrder.filter(r => r !== region);
  const getLabel = (r) =>
    lang === 'es' ? meta[r].label : lang === 'fr' ? meta[r].labelFr : meta[r].labelEn;
  const currentLabel = getLabel(region);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setPhase('modal'), 600);
      return () => clearTimeout(t);
    }
  }, []);

  function activateHint() {
    setShowRegionHint(true);
    setTimeout(() => setShowRegionHint(false), 4500);
  }

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1');
    setPhase('closing');
    // Esperar a que la animación de cierre termine completamente (300ms) + margen
    setTimeout(() => setPhase('done'), 350);
    setTimeout(() => activateHint(), 400);
  }

  function handleGoToRegion(r) {
    localStorage.setItem(STORAGE_KEY, '1');
    if (r === 'ca') setLang('fr');
    if (r === 'us') setLang('en');
    setPhase('closing');
    setTimeout(() => { goToRegion(r); setPhase('done'); }, 350);
    setTimeout(() => activateHint(), 400);
  }

  // Asegurar que el hint no se muestre mientras el modal existe
  useEffect(() => {
    if (phase === 'modal' || phase === 'closing') {
      setShowRegionHint(false);
    }
  }, [phase, setShowRegionHint]);

  if (phase === 'idle' || phase === 'done') return null;

  return (
    <Overlay
      $closing={phase === 'closing'}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <Modal>
        <FlagDiv><FlagImg code={flag} size={48} style={{ borderRadius: 6 }} /></FlagDiv>
        <Title>
          {es ? ('Estas en el sitio para ' + currentLabel)
          : fr ? ('Vous consultez le site pour ' + currentLabel)
               : ('You\'re browsing for ' + currentLabel)}
        </Title>
        <Sub>
          {es ? ('Los precios se muestran en ' + currency.toUpperCase() + ' ' + symbol)
          : fr ? ('Les prix sont affichés en ' + currency.toUpperCase() + ' ' + symbol)
               : ('Prices are shown in ' + currency.toUpperCase() + ' ' + symbol)}
        </Sub>
        <Buttons>
          <BtnPrimary onClick={dismiss}>
            <FlagImg code={flag} size={16} style={{ marginRight: 6, borderRadius: 2 }} />
            {es ? ('Continuar en ' + currentLabel)
            : fr ? ('Continuer pour ' + currentLabel)
                 : ('Continue for ' + currentLabel)}
          </BtnPrimary>
          <Divider><span>{es ? 'o' : fr ? 'ou' : 'or'}</span></Divider>
          {otherRegions.map(r => (
            <BtnSecondary key={r} onClick={() => handleGoToRegion(r)}>
              <FlagImg code={r} size={16} style={{ marginRight: 6, borderRadius: 2 }} />
              {es ? ('Cambiar a ' + getLabel(r))
              : fr ? ('Passer à ' + getLabel(r))
                   : ('Switch to ' + getLabel(r))}
            </BtnSecondary>
          ))}
        </Buttons>
      </Modal>
    </Overlay>
  );
}
